// ============================================
// Triton Instance Details
// ============================================

export interface TritonModelInfo {
  name: string;
  version: string;
  backend: 'tensorrt' | 'onnxruntime' | 'pytorch' | 'tensorflow' | 'python' | 'openvino' | 'fil';
  status: 'ready' | 'loading' | 'unavailable' | 'error';
  endpoint: string;
  inputFormat: string;
  outputFormat: string;
  instances: number;
  dynamicBatching: boolean;
  maxBatchSize: number;
  metrics: {
    inferenceCount: number;
    avgLatencyMs: number;
    p99LatencyMs: number;
    queueTimeMs: number;
  };
}

export interface TritonModelManagement {
  controlMode: 'explicit' | 'poll' | 'none';
  repositoryPath: string;
  pollIntervalSecs?: number;
  endpoints: {
    index: string;
    load: string;
    unload: string;
  };
}

export interface TritonBotPrevention {
  enabled: boolean;
  rateLimit: {
    enabled: boolean;
    requestsPerMinute: number;
    requestsPerHour: number;
    burstLimit: number;
    blockedIPs: string[];
    whitelistedIPs: string[];
  };
  proofOfWork: {
    enabled: boolean;
    difficulty: 'low' | 'medium' | 'high' | 'adaptive';
    algorithm: 'sha256';
    validitySeconds: number;
    exemptAuthenticatedUsers: boolean;
    challengeEndpoint: string;
    protectedEndpoint: string;
  };
  stats: {
    blockedRequests24h: number;
    rateLimitedRequests24h: number;
    powChallengesSolved24h: number;
    avgPowSolveTimeMs: number;
  };
}

export interface TritonProductionMetrics {
  requestsPerSecond: number;
  avgLatencyMs: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  p99LatencyMs: number;
  errorRate: number;
  activeConnections: number;
  queuedRequests: number;
  gpuUtilization: number;
  gpuMemoryUsed: number;
  gpuMemoryTotal: number;
  uptime: string;
  totalInferences24h: number;
}

export interface TritonInstanceDetails {
  serverVersion: string;
  status: 'healthy' | 'degraded' | 'offline';
  models: TritonModelInfo[];
  modelManagement: TritonModelManagement;
  botPrevention: TritonBotPrevention;
  apiKey: string;
  baseEndpoint: string;
  productionMetrics: TritonProductionMetrics;
}

// Mock version history for models (demo mode)
export interface MockModelVersionHistory {
  name: string;
  versions: {
    version: string;
    backend: TritonModelInfo['backend'];
    status: TritonModelInfo['status'];
    uploadedAt: number;
    isActive: boolean;
    maxBatchSize: number;
    files: string[];
    configPbtxt: string;
    metrics: {
      inferenceCount: number;
      avgLatencyMs: number;
      p99LatencyMs: number;
      queueTimeMs: number;
    };
    changelog?: string;
  }[];
  activeVersion: string;
}

// Pre-populated mock version history for demo models
export const getMockModelVersionHistory = (taskId: string): Record<string, MockModelVersionHistory> => ({
  'bge-large-en-v1.5': {
    name: 'bge-large-en-v1.5',
    versions: [
      {
        version: '1',
        backend: 'onnxruntime',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 30, // 30 days ago
        isActive: false,
        maxBatchSize: 16,
        files: ['model.onnx'],
        configPbtxt: `name: "bge-large-en-v1.5"
platform: "onnxruntime_onnx"
max_batch_size: 16`,
        metrics: {
          inferenceCount: 125000,
          avgLatencyMs: 15,
          p99LatencyMs: 52,
          queueTimeMs: 3,
        },
        changelog: 'Initial deployment of BGE embedding model',
      },
      {
        version: '2',
        backend: 'onnxruntime',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 14, // 14 days ago
        isActive: false,
        maxBatchSize: 32,
        files: ['model.onnx', 'tokenizer.json'],
        configPbtxt: `name: "bge-large-en-v1.5"
platform: "onnxruntime_onnx"
max_batch_size: 32
dynamic_batching { preferred_batch_size: [8, 16, 32] }`,
        metrics: {
          inferenceCount: 89000,
          avgLatencyMs: 13,
          p99LatencyMs: 48,
          queueTimeMs: 2,
        },
        changelog: 'Increased batch size and enabled dynamic batching',
      },
      {
        version: '3',
        backend: 'onnxruntime',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 3, // 3 days ago
        isActive: true,
        maxBatchSize: 32,
        files: ['model_optimized.onnx', 'tokenizer.json'],
        configPbtxt: `name: "bge-large-en-v1.5"
platform: "onnxruntime_onnx"
max_batch_size: 32
dynamic_batching { preferred_batch_size: [8, 16, 32] }
optimization { graph_level: 1 }`,
        metrics: {
          inferenceCount: 28450,
          avgLatencyMs: 12,
          p99LatencyMs: 45,
          queueTimeMs: 2,
        },
        changelog: 'Applied ONNX graph optimizations for improved latency',
      },
    ],
    activeVersion: '3',
  },
  'yolov8-detection': {
    name: 'yolov8-detection',
    versions: [
      {
        version: '1',
        backend: 'tensorrt',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 21, // 21 days ago
        isActive: false,
        maxBatchSize: 4,
        files: ['model.plan'],
        configPbtxt: `name: "yolov8-detection"
platform: "tensorrt_plan"
max_batch_size: 4`,
        metrics: {
          inferenceCount: 45000,
          avgLatencyMs: 35,
          p99LatencyMs: 120,
          queueTimeMs: 8,
        },
        changelog: 'Initial YOLOv8 deployment with FP32 precision',
      },
      {
        version: '2',
        backend: 'tensorrt',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 7, // 7 days ago
        isActive: true,
        maxBatchSize: 8,
        files: ['model_fp16.plan', 'labels.txt'],
        configPbtxt: `name: "yolov8-detection"
platform: "tensorrt_plan"
max_batch_size: 8
dynamic_batching { max_queue_delay_microseconds: 100 }`,
        metrics: {
          inferenceCount: 12340,
          avgLatencyMs: 28,
          p99LatencyMs: 85,
          queueTimeMs: 5,
        },
        changelog: 'Converted to FP16 for faster inference, doubled batch size',
      },
    ],
    activeVersion: '2',
  },
  'resnet50-classifier': {
    name: 'resnet50-classifier',
    versions: [
      {
        version: '1',
        backend: 'tensorrt',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 45, // 45 days ago
        isActive: false,
        maxBatchSize: 8,
        files: ['model.plan'],
        configPbtxt: `name: "resnet50-classifier"
platform: "tensorrt_plan"
max_batch_size: 8`,
        metrics: {
          inferenceCount: 150000,
          avgLatencyMs: 12,
          p99LatencyMs: 35,
          queueTimeMs: 2,
        },
        changelog: 'Initial ResNet50 classifier deployment',
      },
      {
        version: '2',
        backend: 'tensorrt',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 20, // 20 days ago
        isActive: false,
        maxBatchSize: 16,
        files: ['model_int8.plan', 'calibration.cache'],
        configPbtxt: `name: "resnet50-classifier"
platform: "tensorrt_plan"
max_batch_size: 16
optimization { cuda_streams: 2 }`,
        metrics: {
          inferenceCount: 95000,
          avgLatencyMs: 9,
          p99LatencyMs: 28,
          queueTimeMs: 1,
        },
        changelog: 'INT8 quantization with calibration for 25% speedup',
      },
      {
        version: '3',
        backend: 'tensorrt',
        status: 'ready',
        uploadedAt: Date.now() - 86400000 * 5, // 5 days ago
        isActive: true,
        maxBatchSize: 16,
        files: ['model_int8_v2.plan', 'calibration.cache', 'preprocessing.py'],
        configPbtxt: `name: "resnet50-classifier"
platform: "tensorrt_plan"
max_batch_size: 16
optimization { cuda_streams: 2 }
instance_group { count: 2 kind: KIND_GPU }`,
        metrics: {
          inferenceCount: 8920,
          avgLatencyMs: 8,
          p99LatencyMs: 25,
          queueTimeMs: 1,
        },
        changelog: 'Added 2 GPU instances for higher throughput',
      },
    ],
    activeVersion: '3',
  },
});

export const mockTritonInstanceDetails = (taskId: string): TritonInstanceDetails => ({
  serverVersion: '2.42.0',
  status: 'healthy',
  baseEndpoint: `https://api.float16.cloud/v2/gpu/${taskId}`,
  models: [
    {
      name: 'bge-large-en-v1.5',
      version: '3', // Active version
      backend: 'onnxruntime',
      status: 'ready',
      endpoint: `https://api.float16.cloud/v2/gpu/${taskId}/models/bge-large-en-v1.5/infer`,
      inputFormat: 'text[]',
      outputFormat: 'float32[1024]',
      instances: 2,
      dynamicBatching: true,
      maxBatchSize: 32,
      metrics: {
        inferenceCount: 28450,
        avgLatencyMs: 12,
        p99LatencyMs: 45,
        queueTimeMs: 2,
      },
    },
    {
      name: 'yolov8-detection',
      version: '2', // Active version
      backend: 'tensorrt',
      status: 'ready',
      endpoint: `https://api.float16.cloud/v2/gpu/${taskId}/models/yolov8-detection/infer`,
      inputFormat: 'image/jpeg',
      outputFormat: 'json (boxes, scores, classes)',
      instances: 1,
      dynamicBatching: true,
      maxBatchSize: 8,
      metrics: {
        inferenceCount: 12340,
        avgLatencyMs: 28,
        p99LatencyMs: 85,
        queueTimeMs: 5,
      },
    },
    {
      name: 'whisper-large-v3',
      version: '1',
      backend: 'python',
      status: 'ready',
      endpoint: `https://api.float16.cloud/v2/gpu/${taskId}/models/whisper-large-v3/infer`,
      inputFormat: 'audio/wav',
      outputFormat: 'json (text, segments)',
      instances: 1,
      dynamicBatching: false,
      maxBatchSize: 1,
      metrics: {
        inferenceCount: 4440,
        avgLatencyMs: 1250,
        p99LatencyMs: 3200,
        queueTimeMs: 120,
      },
    },
    {
      name: 'resnet50-classifier',
      version: '3', // Active version
      backend: 'tensorrt',
      status: 'ready',
      endpoint: `https://api.float16.cloud/v2/gpu/${taskId}/models/resnet50-classifier/infer`,
      inputFormat: 'image/jpeg',
      outputFormat: 'json (class, confidence)',
      instances: 2,
      dynamicBatching: true,
      maxBatchSize: 16,
      metrics: {
        inferenceCount: 8920,
        avgLatencyMs: 8,
        p99LatencyMs: 25,
        queueTimeMs: 1,
      },
    },
    {
      name: 'sentiment-bert',
      version: '1',
      backend: 'onnxruntime',
      status: 'loading',
      endpoint: `https://api.float16.cloud/v2/gpu/${taskId}/models/sentiment-bert/infer`,
      inputFormat: 'text',
      outputFormat: 'json (sentiment, score)',
      instances: 1,
      dynamicBatching: true,
      maxBatchSize: 16,
      metrics: {
        inferenceCount: 0,
        avgLatencyMs: 0,
        p99LatencyMs: 0,
        queueTimeMs: 0,
      },
    },
  ],
  modelManagement: {
    controlMode: 'explicit',
    repositoryPath: '/models',
    endpoints: {
      index: `/v2/gpu/${taskId}/repository/index`,
      load: `/v2/gpu/${taskId}/repository/models/{MODEL_NAME}/load`,
      unload: `/v2/gpu/${taskId}/repository/models/{MODEL_NAME}/unload`,
    },
  },
  botPrevention: {
    enabled: true,
    rateLimit: {
      enabled: true,
      requestsPerMinute: 120,
      requestsPerHour: 3000,
      burstLimit: 20,
      blockedIPs: ['192.168.1.100'],
      whitelistedIPs: ['10.0.0.1', '192.168.1.1'],
    },
    proofOfWork: {
      enabled: true,
      difficulty: 'medium',
      algorithm: 'sha256',
      validitySeconds: 300,
      exemptAuthenticatedUsers: true,
      challengeEndpoint: `/v2/gpu/${taskId}/pow/challenge`,
      protectedEndpoint: `/v2/gpu/${taskId}/pow/models/{MODEL_NAME}/infer`,
    },
    stats: {
      blockedRequests24h: 892,
      rateLimitedRequests24h: 2145,
      powChallengesSolved24h: 15670,
      avgPowSolveTimeMs: 125,
    },
  },
  apiKey: `sk-triton-${taskId.slice(0, 8)}-${Math.random().toString(36).slice(2, 10)}`,
  productionMetrics: {
    requestsPerSecond: 45.2,
    avgLatencyMs: 32,
    p50LatencyMs: 18,
    p95LatencyMs: 85,
    p99LatencyMs: 180,
    errorRate: 0.1,
    activeConnections: 12,
    queuedRequests: 3,
    gpuUtilization: 45,
    gpuMemoryUsed: 24,
    gpuMemoryTotal: 80,
    uptime: '12d 8h 45m',
    totalInferences24h: 54150,
  },
});
